import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Activity, TrendingUp, Wallet, Layers } from "lucide-react";

const money = (v) => (v === null || v === undefined ? "—" : fmtMoney(v));
const num = (v) => (v === null || v === undefined ? "—" : v);

const Stat = ({ label, value, testId, accent }) => (
  <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-lg font-semibold ${accent || "text-slate-100"}`}>{value}</div>
  </div>
);

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between border-b border-[#1E2D4A] py-2 last:border-0">
    <span className={`text-xs ${strong ? "text-slate-200" : "text-slate-500"}`}>{label}</span>
    <span className={`font-mono text-sm ${strong ? "text-cyan-300" : "text-slate-200"}`}>{value}</span>
  </div>
);

const HEALTH = {
  healthy: "text-emerald-300", degraded: "text-amber-300", stale: "text-amber-400",
  error: "text-rose-400", not_connected: "text-slate-400", revoked: "text-rose-400",
};

export default function MolarooFinance() {
  const { data: s } = useQuery({ queryKey: ["molaroo-summary"], queryFn: async () => (await api.get("/molaroo/finance/summary")).data });
  const { data: st } = useQuery({ queryKey: ["molaroo-status"], queryFn: async () => (await api.get("/molaroo/finance/status")).data });
  const sub = s?.subscriptions || {};
  const met = s?.metrics || {};
  const rev = s?.revenue || {};
  const p = s?.profitability || {};

  return (
    <div data-testid="molaroo-finance-page" className="ns-fade">
      <PageHeader title="Molaroo — Finance" subtitle="Aggregate commercial control for the Stripe-billed Molaroo product. Stripe/Molaroo remains the source of truth; NavScora shows commercial totals only.">
        {st && (
          <span className="flex items-center gap-1.5 rounded-lg border border-[#2D3F62] bg-[#0B1120] px-3 py-1.5 text-xs" data-testid="molaroo-health">
            <Activity className={`h-3.5 w-3.5 ${HEALTH[st.connection_health] || "text-slate-400"}`} />
            <span className={HEALTH[st.connection_health] || "text-slate-400"}>{st.health_label}</span>
          </span>
        )}
      </PageHeader>

      {!s?.has_snapshot && (
        <div className="mb-4 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs text-amber-200" data-testid="molaroo-no-data">
          No commercial snapshot received yet for {s?.month}. Figures below use manually-entered Molaroo costs only. Connect Molaroo to receive Stripe commercial totals.
        </div>
      )}

      <SectionTitle className="mb-2 flex items-center gap-2"><Layers className="h-4 w-4 text-cyan-400" /> Subscriptions</SectionTitle>
      <div className="mb-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Active" value={num(sub.active)} testId="molaroo-active" accent="text-emerald-300" />
        <Stat label="Trial" value={num(sub.trial)} testId="molaroo-trial" />
        <Stat label="New" value={num(sub.new)} testId="molaroo-new" />
        <Stat label="Cancelled" value={num(sub.cancelled)} testId="molaroo-cancelled" />
        <Stat label="Past due" value={num(sub.past_due)} testId="molaroo-pastdue" accent="text-amber-300" />
      </div>

      <SectionTitle className="mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-400" /> Recurring metrics</SectionTitle>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Stat label="MRR" value={money(met.mrr)} testId="molaroo-mrr" accent="text-cyan-300" />
        <Stat label="ARR" value={money(met.arr)} testId="molaroo-arr" accent="text-cyan-300" />
        <Stat label="ARPU" value={money(met.arpu)} testId="molaroo-arpu" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="molaroo-revenue-card">
          <SectionTitle className="mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-cyan-400" /> Revenue — commercial snapshot ({s?.currency})</SectionTitle>
          <Row label="Gross sales" value={money(rev.gross_sales)} />
          <Row label="Discounts" value={money(rev.discounts)} />
          <Row label="Refunds" value={money(rev.refunds)} />
          <Row label="Net sales" value={money(rev.net_sales)} strong />
          <Row label="GST collected (as supplied)" value={money(rev.tax_collected)} />
          <Row label="Revenue ex-GST" value={money(rev.revenue_ex_gst)} />
          <Row label="Stripe fees" value={money(rev.stripe_fees)} />
          <Row label="Stripe adjustments" value={money(rev.stripe_adjustments)} />
          <Row label="Stripe net proceeds" value={money(rev.net_proceeds)} strong />
          <p className="mt-2 text-[11px] text-slate-500">Company GST registered: {String(s?.company_gst_registered)}. GST shown only where supplied by the source — never derived.</p>
        </div>

        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="molaroo-profit-card">
          <SectionTitle className="mb-3">Operating contribution</SectionTitle>
          <Row label="Gross revenue" value={money(p.gross_revenue)} />
          <Row label="less Discounts" value={money(p.discounts)} />
          <Row label="less Refunds" value={money(p.refunds)} />
          <Row label="= Net revenue" value={money(p.net_revenue)} strong />
          <Row label="less Stripe fees" value={money(p.stripe_fees)} />
          <Row label="= Net payment revenue" value={money(p.net_payment_revenue)} strong />
          <Row label={`less Allocated Molaroo costs (${s?.operating_cost_count ?? 0})`} value={money(p.allocated_operating_costs)} />
          <Row label="= Operating contribution" value={money(p.operating_contribution)} strong />
          <div className="mt-3 flex items-center justify-between rounded-lg bg-[#0B1120] px-3 py-2">
            <span className="text-xs text-slate-400">Operating margin</span>
            <span className="text-lg font-semibold text-emerald-300" data-testid="molaroo-margin">{p.operating_margin_pct ?? 0}%</span>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Operating contribution — not accounting net profit. Excludes company-level overhead, tax and shared allocations. Costs are managed as Expenses tagged to the Molaroo product.</p>
        </div>
      </div>
    </div>
  );
}
